import { logEvent } from 'firebase/analytics';
import {
  useQuery,
  useMutation,
  useQueryClient,
  MutationOptions,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';
import { QUERY_KEYS } from '../query-keys';
import { analytics } from '../../../firebase';
import { useLearningApi } from './use-learning-api';
import { useErrorContext } from '../../../components/error';
import { useAuthContext } from '../../../auth/auth-provider';
import {
  DeleteResourceBookmarkRespBody,
  DeleteAllResourceBookmarksRespBody,
  HideRecommendationRespBody,
  PostResourceBookmarkRespBody,
  PutUserResourceInteractionBody,
  PutUserResourceInteractionRespBody,
} from '../../../services/learning-api/schema';
import {
  ResourceFacets,
  PaginatedResources,
  BookmarkedResources,
  InProgressResources,
  PostManagerEmailBody,
  ResourceRecommendations,
  GetResourcesPaginatedParamsExtended,
} from '../../../services/learning-api';

export const useGetResourcesPaginated = ({
  pageNumber = 0,
  pageSize = 10,
  ...rest
}: Partial<GetResourcesPaginatedParamsExtended> = {}) => {
  const params = { pageNumber, pageSize, ...rest } as GetResourcesPaginatedParamsExtended;
  const learningApi = useLearningApi();
  const { userId } = useAuthContext();

  logEvent(analytics, 'search', {
    search_query: params.searchQuery,
    category: params.skillCategories,
    mediatype: params.mediaTypes,
    location: params.isInternal,
    sort_order: params.sortOrder,
    sort_field: params.sortField,
    custom_user_id: userId,
  });

  return useQuery<{ resources: PaginatedResources[]; totalCount: number }>({
    queryKey: QUERY_KEYS.learning.getResourcesPaginated(params),
    queryFn: () => learningApi.resourcePaginated.getResourcesPaginated(params),
  });
};

export const useGetResourceFacets = () => {
  const learningApi = useLearningApi();
  return useQuery<ResourceFacets>({
    queryKey: QUERY_KEYS.learning.resourceFacets(),
    queryFn: () => learningApi.resource.getFacets(),
  });
};

export const useGetRecommendations = () => {
  const learningApi = useLearningApi();
  return useQuery<ResourceRecommendations[]>({
    queryKey: QUERY_KEYS.learning.resourceRecommendations(),
    queryFn: () => learningApi.resource.getRecommendations(),
  });
};

// used to optimistically update resource in query cache for instant UI feedback
const updateResourceWithInteraction = (
  oldData: (PaginatedResources | ResourceRecommendations)[],
  resourceInteraction: PutUserResourceInteractionBody
) => {
  return oldData.map((resource) => {
    if (resource.id !== resourceInteraction.resource_id) return resource;

    //only update likes if they have actually changed to avoid UI glitching
    const oldLiked = !!resource.isLikedByUser;
    const newLiked = resourceInteraction.liked;

    let totalLikes = resource.totalLikes ?? 0;
    if (oldLiked !== newLiked) {
      if (newLiked) {
        totalLikes += 1;
      } else {
        totalLikes = Math.max(0, totalLikes - 1);
      }
    }

    return {
      ...resource,
      isLikedByUser: newLiked,
      totalLikes,
      userProgressStatus: resourceInteraction.status,
    };
  });
};

export const usePutUserResourceInteraction = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const learningApi = useLearningApi();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (
      data: PutUserResourceInteractionBody
    ): Promise<PutUserResourceInteractionRespBody> => {
      return learningApi.resource.putUserResourceInteraction(data);
    },

    onMutate: async (resourceInteraction: PutUserResourceInteractionBody) => {
      const queryKeys = [
        QUERY_KEYS.learning.inProgressResources(),
        QUERY_KEYS.learning.resourcesPaginated(),
        QUERY_KEYS.learning.resourceRecommendations(),
      ];

      await Promise.all(queryKeys.map((key) => queryClient.cancelQueries({ queryKey: key })));
      const previousQueryData = storePreviousQueryData(queryKeys, queryClient);

      queryClient.setQueryData(
        QUERY_KEYS.learning.inProgressResources(),
        (oldData: PaginatedResources[]) =>
          oldData ? updateResourceWithInteraction(oldData, resourceInteraction) : oldData
      );

      queryClient.setQueriesData(
        { queryKey: QUERY_KEYS.learning.resourcesPaginated() },
        (oldData: { resources: PaginatedResources[]; totalCount: number } | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            resources: updateResourceWithInteraction(oldData.resources, resourceInteraction),
          };
        }
      );

      queryClient.setQueryData(
        QUERY_KEYS.learning.resourceRecommendations(),
        (oldData: ResourceRecommendations[]) =>
          oldData ? updateResourceWithInteraction(oldData, resourceInteraction) : oldData
      );

      return { previousQueryData };
    },
    onError: (error, _, context) => {
      if (context?.previousQueryData) {
        rollbackQueryData(context.previousQueryData, queryClient);
      }

      options?.onError?.(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        type: 'active',
        queryKey: QUERY_KEYS.learning.resourceRecommendations(),
      });

      queryClient.invalidateQueries({
        type: 'active',
        queryKey: QUERY_KEYS.learning.inProgressResources(),
      });

      queryClient.invalidateQueries({
        type: 'active',
        queryKey: QUERY_KEYS.learning.resourcesPaginated(),
      });
    },
  });

  return {
    putUserResourceInteraction: mutation.mutateAsync,
    isSavingInteractionLoading: mutation.status === 'pending',
  };
};

export const usePostManagerEmail = (
  mutationOptions: MutationOptions<PostManagerEmailBody, Error, any> = {}
) => {
  const learningApi = useLearningApi();
  return useMutation({
    mutationFn: (payload: PostManagerEmailBody) => learningApi.resource.emailManager(payload),
    ...mutationOptions,
  });
};

export const usePostHideRecommendation = () => {
  const queryClient = useQueryClient();
  const { setError } = useErrorContext();
  const learningApi = useLearningApi();

  const mutation = useMutation({
    mutationFn: async (recommendationId: string): Promise<HideRecommendationRespBody> => {
      return learningApi.resource.postHideRecommendation({ recommendationId });
    },
    onSuccess: (updated: HideRecommendationRespBody) => {
      const updatedId = updated.resource?.resourceId;

      queryClient.setQueryData(
        QUERY_KEYS.learning.resourceRecommendations(),
        (oldResources: ResourceRecommendations[]) => {
          return oldResources.map((resource) =>
            resource.id === updatedId ? { ...resource, recommendationHidden: true } : resource
          );
        }
      );
    },
    onError: (error) => {
      setError({
        code: 500,
        message: 'Failed to hide learning recommendation',
      });
      console.error('Error when hiding learning recommendation:', error.message);
    },
  });

  return {
    postHideRecommendation: mutation.mutateAsync,
  };
};

//  Handle fetching, creation, and deletion of bookmarked resources

const storePreviousQueryData = (queryKeys: QueryKey[], queryClient: QueryClient) => {
  const previousData = queryKeys.map((queryKey) => {
    return {
      queryKey: queryKey,
      data: queryClient.getQueryData(queryKey),
    };
  });

  return previousData;
};

const updateBookmarkInResource = (
  oldResources: ResourceRecommendations[] | PaginatedResources[],
  updatedId: string,
  bookmarkedStatus: boolean
) => {
  return oldResources.map((resource) =>
    resource.id === updatedId ? { ...resource, isBookmarked: bookmarkedStatus } : resource
  );
};

const handleBookmarkUpdate = (
  queryClient: QueryClient,
  updatedId: string,
  newBookmarkedStatus: boolean
) => {
  queryClient.setQueriesData(
    {
      type: 'active',
      queryKey: QUERY_KEYS.learning.resourceRecommendations(),
    },
    (oldResources: ResourceRecommendations[]) =>
      oldResources
        ? updateBookmarkInResource(oldResources, updatedId, newBookmarkedStatus)
        : oldResources
  );

  queryClient.setQueriesData(
    {
      type: 'active',
      queryKey: QUERY_KEYS.learning.resourcesPaginated(),
    },
    (oldData: { resources: PaginatedResources[]; totalCount: number }) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        resources: updateBookmarkInResource(oldData.resources, updatedId, newBookmarkedStatus),
      };
    }
  );
};

const rollbackQueryData = (
  previousData: { queryKey: QueryKey; data: unknown }[],
  queryClient: QueryClient
) => {
  previousData.forEach(({ queryKey, data }) => queryClient.setQueryData(queryKey, data));
};

const removeAllBookmarksInResource = (
  oldResources: ResourceRecommendations[] | PaginatedResources[]
) => {
  return oldResources.map((resource) => ({ ...resource, isBookmarked: false }));
};

export const useGetBookmarkedResources = () => {
  const learningApi = useLearningApi();

  return useQuery<BookmarkedResources[]>({
    queryKey: QUERY_KEYS.learning.resourceBookmarks(),
    queryFn: () => learningApi.resource.getResourceBookmarks(),
  });
};

export const usePostResourceBookmark = () => {
  const queryClient = useQueryClient();
  const learningApi = useLearningApi();
  const { setError } = useErrorContext();

  const mutation = useMutation({
    mutationFn: async (resourceId: string): Promise<PostResourceBookmarkRespBody> => {
      return learningApi.resource.postResourceBookmark({ resourceId });
    },
    onMutate: async (updatedId) => {
      const queryKeys = [
        QUERY_KEYS.learning.resourceRecommendations(),
        QUERY_KEYS.learning.resourcesPaginated(),
      ];

      await Promise.all(queryKeys.map((key) => queryClient.cancelQueries({ queryKey: key })));

      const previousQueryData = storePreviousQueryData(queryKeys, queryClient);

      handleBookmarkUpdate(queryClient, updatedId, true);

      return {
        previousQueryData,
      };
    },
    onError: (error, _resourceId, context) => {
      if (context?.previousQueryData) {
        rollbackQueryData(context.previousQueryData, queryClient);
      }

      setError({
        code: 500,
        message: 'Failed to bookmark resource',
      });
      console.error('An error occurred when bookmarking the learning resource', error.message);
    },
    onSettled: () => {
      // Invalidate so that when a bookmark is deleted from the bookmarks page
      // and the undo action is clicked on the toast message, the page is reloaded dyanmically
      queryClient.invalidateQueries({
        type: 'active',
        queryKey: QUERY_KEYS.learning.resourceBookmarks(),
      });
    },
  });

  return {
    postResourceBookmark: mutation.mutateAsync,
  };
};

export const useDeleteResourceBookmark = () => {
  const queryClient = useQueryClient();
  const learningApi = useLearningApi();
  const { setError } = useErrorContext();

  const mutation = useMutation({
    mutationFn: async (resourceId: string): Promise<DeleteResourceBookmarkRespBody> => {
      return learningApi.resource.deleteResourceBookmark({ resourceId });
    },
    onMutate: async (updatedId) => {
      const queryKeys = [
        QUERY_KEYS.learning.resourceRecommendations(),
        QUERY_KEYS.learning.resourcesPaginated(),
        QUERY_KEYS.learning.resourceBookmarks(),
      ];

      await Promise.all(queryKeys.map((key) => queryClient.cancelQueries({ queryKey: key })));

      const previousQueryData = storePreviousQueryData(queryKeys, queryClient);

      handleBookmarkUpdate(queryClient, updatedId, false);

      // Update the bookmarks cache in the delete flow as it is a delete specific action
      queryClient.setQueryData(
        QUERY_KEYS.learning.resourceBookmarks(),
        (oldData: BookmarkedResources[]) =>
          oldData ? oldData.filter((resource) => resource.id !== updatedId) : oldData
      );

      return {
        previousQueryData,
      };
    },
    onError: (error, _resourceId, context) => {
      if (context?.previousQueryData) {
        rollbackQueryData(context.previousQueryData, queryClient);
      }

      setError({
        code: 500,
        message: 'Failed to remove learning resource bookmark',
      });
      console.error(
        'An error occurred when removing the learning resource from bookmarks',
        error.message
      );
    },
  });

  return {
    deleteResourceBookmark: mutation.mutateAsync,
  };
};

export const useDeleteAllResourceBookmarks = () => {
  const queryClient = useQueryClient();
  const { setError } = useErrorContext();
  const learningApi = useLearningApi();

  const queryKeys = [
    QUERY_KEYS.learning.resourceBookmarks(),
    QUERY_KEYS.learning.resourceRecommendations(),
    QUERY_KEYS.learning.resourcesPaginated(),
  ];

  const mutation = useMutation({
    mutationFn: async (): Promise<DeleteAllResourceBookmarksRespBody> => {
      return learningApi.resource.deleteAllResourceBookmarks();
    },

    onMutate: async () => {
      await Promise.all(queryKeys.map((key) => queryClient.cancelQueries({ queryKey: key })));
      const previousQueryData = storePreviousQueryData(queryKeys, queryClient);

      queryClient.setQueryData<BookmarkedResources[]>(QUERY_KEYS.learning.resourceBookmarks(), []);

      queryClient.setQueryData(
        QUERY_KEYS.learning.resourceRecommendations(),
        (oldData: ResourceRecommendations[]) => {
          return oldData ? removeAllBookmarksInResource(oldData) : oldData;
        }
      );

      queryClient.setQueryData(
        QUERY_KEYS.learning.resourcesPaginated(),
        (oldData: ResourceRecommendations[]) => {
          return oldData ? removeAllBookmarksInResource(oldData) : oldData;
        }
      );

      return {
        previousQueryData,
      };
    },

    onError: (error, _variables, context) => {
      if (context?.previousQueryData) {
        rollbackQueryData(context.previousQueryData, queryClient);
      }
      setError({
        code: 500,
        message: 'Failed to remove all bookmarked resources',
      });
      console.error(
        'An error occurred when removing the learning resources from bookmarks',
        error.message
      );
    },

    onSettled: () => {
      queryKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
  });

  return {
    deleteAllResourceBookmarks: mutation.mutateAsync,
  };
};
export const useGetInProgressResources = () => {
  const learningApi = useLearningApi();

  return useQuery<InProgressResources[]>({
    queryKey: QUERY_KEYS.learning.inProgressResources(),
    queryFn: () => learningApi.resource.getInProgressResources(),
  });
};
